import Mustache from 'mustache';
import stringToHtml from './stringToHtml';
import { FileUploader } from './FileUploader';

interface VoltageParams {
  values?: Object,
  affected?: string[]
}
export class CommissioningReport {

  private ajaxEndpoint: string;
  private language: string;
  private form: Element;
  private reportSaveButton: Element;
  private reportSubmitButton: Element;
  private pressedButton: Element;
  private resultTemplateSent: HTMLElement;
  private resultTemplateSaved: HTMLElement;
  private voltageControl: NodeList;
  private voltageControlUnit: HTMLInputElement;
  private voltageControlParams: VoltageParams | false;
  private errorContainer: HTMLElement;
  private filesField: HTMLElement;
  private fileUploader: FileUploader;

  constructor() {
    this.ajaxEndpoint = '/stage-ajax/commission-report/';
    this.language = document.documentElement.lang;
    this.form = document.getElementById('commissioning-report-form') as HTMLFormElement;
    this.reportSaveButton = document.querySelector('.js-commissioning-report-save');
    this.reportSubmitButton = document.querySelector('.js-commissioning-report-submit');
    this.resultTemplateSent = document.getElementById('mustache-commissioning-report-sent');
    this.resultTemplateSaved = document.getElementById('commissioning-report-saved');

    // Update energy unit
    this.voltageControl = this.form.querySelectorAll('input[name="calibration_ctrl_voltage_control"]')
    this.voltageControlUnit = this.form.querySelector('input[name="calibration_ctrl_voltage_unit"]')
    this.voltageControlParams = this.parseVoltageParams(this.voltageControlUnit);

    this.errorContainer = document.getElementById('js-contact-form-error');
    this.filesField = this.form.querySelector('.js-files-field');

    if (this.filesField) {
      this.fileUploader = new FileUploader(this.filesField, this.errorContainer);
    }
  }

  /**
   * Init
   */
  public init() {
    const selected = <HTMLInputElement>Array.from(this.voltageControl).find( (e:HTMLInputElement) => e.checked)
    if (selected && this.voltageControlParams) {
      const unit = this.voltageControlParams.values[selected.value]
      this.setVoltaConrolUnit(unit, this.voltageControlParams.affected)
    }

    if (this.filesField) {
      this.fileUploader.attachListeners();
    }
  }

  /**
   * Attach event listeners to elements
   */
  public attachListeners() {
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
    });
    this.reportSaveButton?.addEventListener('click', (e) => {
      e.preventDefault();
      this.pressedButton = <Element>e.target;
      this.submitOrder('saveDraft');
      this.resultTemplateSaved.innerText = '';
    });
    this.reportSubmitButton?.addEventListener('click', (e) => {
      e.preventDefault();
      this.pressedButton = <Element>e.target;
      this.submitOrder('sendReport');
    });
    this.voltageControl.forEach( input => {
      input.addEventListener('click', function(e) {
        if (!this.voltageControlParams) {
          return
        }
        const target = <HTMLInputElement>e.target
        const unit = this.voltageControlParams.values[target.value]
        this.setVoltaConrolUnit(unit, this.voltageControlParams.affected)
      }.bind(this))
    })
  }

  /**
   * Set voltage unit for elements and hidden input
   */
  private setVoltaConrolUnit(unit:string, affected:string[]):void {
    if (!affected.length || !unit) {
      return
    }

    // Set the value for hidden input
    this.voltageControlUnit.value = unit

    affected.forEach( (element:string) => {
      const target = document.querySelector(`input[name="${element}"]`) as HTMLInputElement
      const unitSpan = target.nextElementSibling as HTMLElement
      if (unitSpan && unitSpan.classList.contains('js-unit')) {
        unitSpan.innerText = unit
      }
    })
  }

  /**
   * Parse data from dataset
   */
  private parseVoltageParams(htmlInput:HTMLInputElement):VoltageParams | false {
    if (!htmlInput || !htmlInput.dataset.affected || !htmlInput.dataset.unitValues ) {
      return false
    }
    const affectedElements = JSON.parse(htmlInput.dataset.affected)
    const unitValues = JSON.parse(htmlInput.dataset.unitValues)

    return {
      affected: affectedElements,
      values: unitValues
    }
  }

  /**
   * Submit form
   *
   */
  public async submitOrder(action) {
    const formData = new FormData(<HTMLFormElement>this.form);

    // Validate serial number with slug
    const serialNumber = formData.get('serial_number');
    const pathName = window.location.pathname;
    const pathArray = pathName.split('/');
    const slug = pathArray[pathArray.length - 2];
    if (serialNumber !== slug) {
      this.throwError('Something went wrong : ' + 'Serial number does not match with page url');
      return false;
    }

    this.pressedButton.classList.add('button--loading');
    this.pressedButton.setAttribute('disabled', 'true');

    // handle files upload inputs separately to avoid sending incomplete file data
    formData.delete('studioFiles[]')

    const uploadFiles = this.fileUploader.getFiles();
    if (Object.values(uploadFiles).length) {
      Object.values(uploadFiles).forEach((file: File) => {
        formData.append('studioFiles[]', file)
      })
    }

    let object = {};
    formData.forEach((value, key) => {
      if(key.includes('[]')) {
        const cleanKey = key.replace('[]', '');
        //create value as an array
        object[cleanKey] ? object[cleanKey].push(value) : object[cleanKey] = [value];
      } else {
        object[key] = value;
      }
    });

    // if no files were added add an empty array to the object
    if (!object['studioFiles']) {
      object['studioFiles'] = [];
    }

    let json = JSON.stringify(object);
    formData.append('stage-language', this.language)
    formData.append('action', action)
    formData.append('inputsData', json)

    // reset uplodaFiles object
    this.fileUploader.clearFiles();

    try {
      const result = await this.makeAjaxRequest(formData);

      // notify user that the report has been saved
      if (result.status === 'saved') {
        this.resultTemplateSaved.innerText = result.message;

        const studioFiles = result.studioFiles;
        this.fileUploader.updateFilesData(studioFiles);
      }

      // clear cart page and show order details
      if (result.status === 'success') {

        const resObj = JSON.parse(result.result);

        const resultData = {
          title: result.message,
          warranty : true,
          formSuccess: true,
          link: resObj.machineCardLink,
          name: resObj.machineCardLinkName,
          pdfUrl: resObj.pdfFileReference
        };
        const resultElement:any = Mustache.render(this.resultTemplateSent.innerText, resultData);

        this.form.replaceWith(stringToHtml(resultElement));
      }
      // error -> show error field
      if (result.status === 'error') {
        throw new Error(result.message);
      }

    } catch (error) {
      this.throwError('Something went wrong : ' + error.message);
    } finally {
      this.pressedButton.classList.remove('button--loading');
      this.pressedButton.removeAttribute('disabled')
    }

  }

  private async throwError(message) {
    this.errorContainer.innerHTML = message;
  }

  /**
   * Make ajax request
   *
   * @param params parameters as object or serialized string
   * @returns {object} return object should contain status, message, data
   */
  private async makeAjaxRequest(params: FormData){

    if (!this.ajaxEndpoint) {
      throw new Error('Form submission failed. Endpoint undefined.');
    }

    return fetch(this.ajaxEndpoint, {
      method: 'POST',
      body: params,
    }).then(response => response.json()).then(data => {
      if (data.status === 200) {
        return data.responseText
      }
      else {
        return data
      }
    })
  }

}
